'use client';

import { useQuery } from '@apollo/client';
import { ChangeEvent, MouseEvent, useState } from 'react';
import {
  Controller,
  ControllerRenderProps,
  useFormContext
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  RegionsQuery,
  RegionsQueryVariables
} from '@/__generated_graphql__/graphql';
import { useRegionQueryState } from '@/components/Filters/FiltersUiState';
import { Checkbox } from '@/components/ui/Checkbox/Checkbox';
import { CrossIcon, SearchIcon } from '@/components/ui/Icons/Icons';
import { Typography } from '@/components/ui/Typography/Typography.styles';
import { GET_REGIONS } from '@/graphql/queries';
import { FormValues } from '@/types';

import * as S from './RegionsFilter.styles';

interface Region {
  id: string;
  name: string;
}

export const RegionsFilter = () => {
  const { t } = useTranslation();
  const { data } = useQuery<RegionsQuery, RegionsQueryVariables>(GET_REGIONS);
  const { control } = useFormContext<Pick<FormValues, 'regions'>>();

  const [query, setQuery] = useRegionQueryState();
  const [isFocused, setIsFocused] = useState(false);

  if (!data?.regions) {
    return null;
  }

  const handleRegionToggle = (
    region: Region,
    field: ControllerRenderProps<Pick<FormValues, 'regions'>>
  ) => {
    const computedValue = Array.isArray(field.value) ? field.value : [];
    const exists = computedValue.some(selected => selected.id === region.id);

    const updatedSelection = exists
      ? computedValue.filter(selected => selected.id !== region.id)
      : [...computedValue, region];

    field.onChange(updatedSelection);
  };

  const handleInputChange = ({
    target: { value }
  }: ChangeEvent<HTMLInputElement>) => {
    setQuery(value);
  };

  const handleToggleFocus = () => {
    setIsFocused(prev => !prev);
  };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setQuery('');
  };

  const filteredRegions = data.regions.filter(({ name }: Region) => {
    if (!name) return;
    return name.toLowerCase().includes(query.toLowerCase());
  });

  return (
    <S.Wrapper>
      <S.SearchInputWrapper $isFocused={isFocused}>
        <SearchIcon />
        <S.SearchInput
          type="text"
          placeholder={t('Filter.city_search_placeholder')}
          value={query}
          data-testid="citySearch"
          onChange={handleInputChange}
          onFocus={handleToggleFocus}
          onBlur={handleToggleFocus}
        />
        {!!query && (
          <S.ClearButton onClick={handleClear} data-testid="clear-button">
            <CrossIcon />
          </S.ClearButton>
        )}
      </S.SearchInputWrapper>
      <Controller
        name="regions"
        control={control}
        render={({ field }) => (
          <S.RegionsContainer>
            {filteredRegions.length ? (
              filteredRegions.map((region: Region) => (
                <S.RegionItem key={region.id}>
                  <Checkbox
                    isChecked={field?.value?.some(({ id }) => id === region.id)}
                    onChange={() => handleRegionToggle(region, field)}
                  />
                  <S.RegionLabel>{region.name}</S.RegionLabel>
                </S.RegionItem>
              ))
            ) : (
              <Typography variant="body3" data-testid="no-result">
                {t('RegionsFilter.no_result')}
              </Typography>
            )}
          </S.RegionsContainer>
        )}
      />
    </S.Wrapper>
  );
};
